/**
 * Chapter openings (Styling ▸ Paragraphs): when the shell asks for one
 * (`EditorChapterOpening`), marks the initial (`zw-opening-initial`) and / or
 * the small-caps lead-in (`zw-opening-lead`) of the document's first
 * paragraph; `prose.css` styles them. Nothing is decorated otherwise, so
 * other documents' spans stay whole. (Not `::first-letter`: a leading comment
 * anchor or image would defeat it.)
 */

import type { Node as PMNode } from "prosemirror-model";
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { initialLength, leadInLength } from "../../lib/chapterOpening";
import type { EditorChapterOpening } from "../port";

const openingLeadKey = new PluginKey<DecorationSet>("zwOpeningLead");

/** Transaction meta that re-reads the opening (`setChapterOpening`). */
export const refreshOpeningMeta = [openingLeadKey, true] as const;

type OpeningPart = {
  /** Characters of the part at the start of the paragraph's text. */
  length: (text: string) => number;
  className: string;
};

function openingParts(opening: EditorChapterOpening): OpeningPart[] {
  const parts: OpeningPart[] = [];
  const { leadIn } = opening;
  if (leadIn !== "none") {
    parts.push({
      length: (text) => leadInLength(text, leadIn),
      className: "zw-opening-lead",
    });
  }
  if (opening.initial === true) {
    parts.push({ length: initialLength, className: "zw-opening-initial" });
  }
  return parts;
}

/** The paragraph's text with each character's document position; a line
 * break reads as a space, other inline leaves (images, anchors) as nothing. */
function textWithPositions(
  paragraph: PMNode,
  start: number,
): { text: string; positions: number[] } {
  let text = "";
  const positions: number[] = [];
  paragraph.forEach((child, offset) => {
    if (child.isText) {
      const value = child.text ?? "";
      for (let i = 0; i < value.length; i += 1) {
        positions.push(start + offset + i);
      }
      text += value;
    } else if (child.type.name === "hard_break") {
      positions.push(start + offset);
      text += " ";
    }
  });
  return { text, positions };
}

/** Contiguous [from, to) ranges covering `positions`, so inline atoms in
 * between (a comment anchor, an image) stay out of the decoration. */
function textRuns(positions: readonly number[]): [number, number][] {
  const runs: [number, number][] = [];
  for (const pos of positions) {
    const last = runs[runs.length - 1];
    if (typeof last !== "undefined" && last[1] === pos) last[1] = pos + 1;
    else runs.push([pos, pos + 1]);
  }
  return runs;
}

/** Lead-ins of the first top-level paragraph (the one `p:first-of-type`
 * styles), even when an empty one comes first. */
function openingDecorations(
  state: EditorState,
  opening: EditorChapterOpening,
): DecorationSet {
  const parts = openingParts(opening);
  if (parts.length === 0) return DecorationSet.empty;
  let paragraph: { node: PMNode; start: number } | null = null;
  state.doc.forEach((block, offset) => {
    if (paragraph === null && block.type.name === "paragraph") {
      paragraph = { node: block, start: offset + 1 };
    }
  });
  if (paragraph === null) return DecorationSet.empty;
  const { node, start } = paragraph;
  const { text, positions } = textWithPositions(node, start);
  const decorations = parts.flatMap(({ length, className }) =>
    textRuns(positions.slice(0, length(text))).map(([from, to]) =>
      Decoration.inline(from, to, { class: className }),
    ),
  );
  return DecorationSet.create(state.doc, decorations);
}

export function openingLeadPlugin(
  opening: () => EditorChapterOpening,
): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: openingLeadKey,
    state: {
      init: (_, state) => openingDecorations(state, opening()),
      apply: (tr, previous, _, state) =>
        tr.docChanged || tr.getMeta(openingLeadKey) === true
          ? openingDecorations(state, opening())
          : previous,
    },
    props: {
      decorations: (state) => openingLeadKey.getState(state),
    },
  });
}
